import { parse } from 'url';

import {
  filterTypes,
  FilterConfig,
  FilterValue,
  MenuFilterValue,
  SelectFilterValue,
  RangeFilterValue,
  BooleanFilterValue,
} from './Filter/types';

import { State } from './Filter/index';

interface Hash<V> {
  [key: string]: V;
}

interface KeyValueObject<V> {
  key: string;
  value: V;
}

type KeyValueArray<V> = KeyValueObject<V>[];

// Array/Object helpers
function objectToKeyValueArray<V>(object: Hash<V>): KeyValueArray<V> {
  return Object.keys(object).map((key) => ({ key, value: object[key] }));
}

function keyValueArrayToObject<V>(array: KeyValueArray<V>): Hash<V> {
  return array.reduce(
    (prev, { key, value }) => ({ ...prev, [key]: value }),
    {}
  );
}

type Generator = (value: FilterValue) => string;
type Parser = (value: string) => FilterValue;
export interface FinderState {
  sorting?: string;
  searching?: string;
  values: State;
}

// Parse url format into state
const parsers = {
  [filterTypes.BOOLEAN]: (value: string): boolean => value === '1',
  [filterTypes.MENU]: (value: string): MenuFilterValue => {
    const [menu, subMenuString] = value.split(':');

    return {
      menu,
      subMenu:
        subMenuString && subMenuString.length ? subMenuString.split(',') : [],
    };
  },
  [filterTypes.MULTISELECT]: (value: string): SelectFilterValue =>
    value && value.length ? value.split(',') : [],
  [filterTypes.SELECT]: (value: string): SelectFilterValue =>
    value && value.length ? value.split(',').slice(0, 1) : [],
  [filterTypes.RANGE]: (value: string): RangeFilterValue => {
    const [min, max] = (
      (value && value.length && value.toString()) ||
      ''
    ).split('-');
    return {
      min: (min && parseFloat(min)) || 0,
      max: (max && parseFloat(max)) || 0,
    };
  },
};

// Serialize state format into url format
const generators = {
  [filterTypes.BOOLEAN]: (value: BooleanFilterValue) => (value ? '1' : '0'),
  [filterTypes.MENU]: (value: MenuFilterValue) =>
    `${value.menu}:${value.subMenu
      .map((v) => encodeURIComponent(v))
      .join(',')}`,
  [filterTypes.MULTISELECT]: (value: SelectFilterValue) =>
    value.map((v) => encodeURIComponent(v)).join(','),
  [filterTypes.SELECT]: (value: SelectFilterValue) =>
    encodeURIComponent(value[0]),
  [filterTypes.RANGE]: (value: RangeFilterValue) => `${value.min}-${value.max}`,
};

// Make sure the urls are always sorted by key
function sortByKey<V>(
  a: { key: string; value: V },
  b: { key: string; value: V }
): number {
  return a.key > b.key ? 1 : -1;
}

export const generateQueryString = (
  filters: FilterConfig[],
  state: FinderState
): string => {
  const { sorting, searching, values } = state;

  const search = [
    ...objectToKeyValueArray(values)
      .sort((...args) => sortByKey<FilterValue>(...args))
      .map(({ key, value }) => {
        const filter = filters.find(({ id }) => id === key);
        const generator = filter && (generators[filter.type] as Generator);

        if (!generator || value === undefined || value === null) {
          return undefined;
        }

        return { key, value: generator(value) };
      })
      .filter((v) => v !== undefined),
    ...(sorting && sorting !== 'null'
      ? [{ key: 'sorting', value: encodeURIComponent(sorting) }]
      : []),
    ...(searching
      ? [{ key: 'searching', value: encodeURIComponent(searching) }]
      : []),
  ]
    .map((item) => item && `${item.key}=${item.value}`)
    .join('&');

  return `${search.length ? `?${search}` : ''}`;
};

export const parseQueryString = (
  filters: FilterConfig[],
  url: string
): FinderState => {
  const parsedUrl = parse(url, true);

  const { sorting, searching, ...attributeValues } = parsedUrl.query;

  const valueArray = objectToKeyValueArray(attributeValues)
    .sort(sortByKey)
    .map(({ key, value }) => {
      const filter = filters.find(({ id }) => id === key);
      const parser = filter && (parsers[filter.type] as Parser);

      if (!parser || value === undefined || value === null) {
        return undefined;
      }

      return { key, value: parser(value.toString()) };
    })
    .filter((v) => v !== undefined) as KeyValueArray<FilterValue>;

  const values = keyValueArrayToObject<FilterValue>(valueArray);

  const ret: FinderState = {
    values,
  };

  if (sorting !== undefined) {
    ret.sorting = String(sorting);
  }

  if (searching !== undefined) {
    ret.searching = String(searching);
  }

  return ret;
};

const trimSlashes = (str: string): string => str.replace(/^\/|\/$/, '');

export const generateRoute = (chunks: string[] = []): string => {
  return trimSlashes(chunks.join('/'));
};

export const parseRoute = (url = ''): string[] => {
  return trimSlashes(url).split('/');
};
